import { Puzzle } from "./types";
import { idx, peers } from "./util";

/** The mutable solving state for one puzzle: 81 cells, each a given or fillable. */
export class Board {
  readonly given: boolean[] = [];
  readonly value: (string | null)[] = [];
  readonly pencil: Set<string>[] = [];
  readonly solution: string[]; // 81 letters, row-major

  constructor(readonly puzzle: Puzzle) {
    this.solution = puzzle.solution.join("").split("");
    const givens = new Set(puzzle.givens.map(([r, c]) => idx(r, c)));
    for (let i = 0; i < 81; i++) {
      this.given[i] = givens.has(i);
      this.value[i] = this.given[i] ? this.solution[i] : null;
      this.pencil[i] = new Set();
    }
  }

  set(i: number, letter: string | null): void {
    if (this.given[i]) return;
    this.value[i] = letter;
    if (letter) this.pencil[i].clear();
  }

  togglePencil(i: number, letter: string): void {
    if (this.given[i] || this.value[i]) return;
    const s = this.pencil[i];
    if (s.has(letter)) s.delete(letter);
    else s.add(letter);
  }

  /** Put a cell back to a prior (value, pencil) snapshot — used by Undo. */
  restore(i: number, value: string | null, pencil: Set<string>): void {
    if (this.given[i]) return;
    this.value[i] = value;
    this.pencil[i] = new Set(pencil);
  }

  /** Replace a cell's pencil set wholesale — used by Prefill candidates. */
  setPencil(i: number, letters: Set<string>): void {
    if (this.given[i] || this.value[i]) return;
    this.pencil[i] = new Set(letters);
  }

  clearAll(): void {
    for (let i = 0; i < 81; i++)
      if (!this.given[i]) {
        this.value[i] = null;
        this.pencil[i].clear();
      }
  }

  /** Cells whose letter duplicates a peer's (a sudoku-rule violation). */
  conflicts(): Set<number> {
    const bad = new Set<number>();
    for (let i = 0; i < 81; i++) {
      const v = this.value[i];
      if (!v) continue;
      for (const j of peers(i))
        if (this.value[j] === v) {
          bad.add(i);
          bad.add(j);
        }
    }
    return bad;
  }

  /** Filled cells that don't match the solution (for an explicit "Check"). */
  wrongCells(): number[] {
    const out: number[] = [];
    for (let i = 0; i < 81; i++)
      if (this.value[i] && this.value[i] !== this.solution[i]) out.push(i);
    return out;
  }

  /** A printed word is "found" once its solution cells all hold the right letters. */
  foundWords(): Set<string> {
    const found = new Set<string>();
    for (const w of this.puzzle.words) {
      const cells = this.puzzle.loops[w];
      if (cells && cells.every(([r, c]) => this.value[idx(r, c)] === this.solution[idx(r, c)]))
        found.add(w);
    }
    return found;
  }

  filledCount(): number {
    let n = 0;
    for (let i = 0; i < 81; i++) if (this.value[i]) n++;
    return n;
  }

  isComplete(): boolean {
    for (let i = 0; i < 81; i++) if (this.value[i] !== this.solution[i]) return false;
    return true;
  }
}
